import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Security,
  Smartphone,
  Key,
  Close,
  ArrowBack
} from '@mui/icons-material';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  border: 'none',
  overflow: 'hidden',
};

const TwoFactorModal: React.FC = () => {
  const theme = useTheme();
  const {
    isTwoFactorModalOpen,
    closeTwoFactorModal,
    verifyTwoFactor,
    twoFactorUserId,
    twoFactorError,
    clearTwoFactorError,
  } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  // Helper: Verify 2FA code
  const verifyTotpCode = async (userId: string, code: string) => {
    await verifyTwoFactor(userId, code);
  };

  const verifyBackupCode = async (userId: string, code: string) => {
    await verifyTwoFactor(userId, undefined, code);
  };

  const handleVerify = async () => {
    if (!twoFactorUserId) {
      return;
    }
    setLoading(true);
    try {
      if (useBackup) {
        await verifyBackupCode(twoFactorUserId, code);
      } else {
        await verifyTotpCode(twoFactorUserId, code);
      }
      // Modal se automatski zatvara na uspeh kroz AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    clearTwoFactorError();
    setCode('');
    setUseBackup(false);
    closeTwoFactorModal();
  };

  const handleToggleMode = () => {
    setUseBackup(!useBackup);
    setCode('');
    clearTwoFactorError();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code && !loading) {
      handleVerify();
    }
  };

  return (
    <Modal
      open={isTwoFactorModalOpen}
      onClose={handleClose}
      aria-labelledby="two-factor-modal-title"
      sx={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
      }}
    >
      <Box sx={style}>
        <Paper sx={{ height: '100%', borderRadius: 3 }}>
          {/* Header */}
          <Box
            sx={{
              background: useBackup
                ? 'linear-gradient(135deg, #ff7043 0%, #ff8a65 100%)'
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: 'white',
              p: 3,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <Close />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {useBackup ? (
                <Key sx={{ fontSize: 28, mr: 2 }} />
              ) : (
                <Security sx={{ fontSize: 28, mr: 2 }} />
              )}
              <Typography variant="h5" fontWeight={600}>
                Dvofaktorska autentifikacija
              </Typography>
            </Box>

            <Chip
              icon={useBackup ? <Key /> : <Smartphone />}
              label={useBackup ? 'Rezervni kod' : 'Autentifikator'}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'white',
                }
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              {useBackup
                ? 'Unesite jedan od vaših rezervnih kodova. Svaki kod se može koristiti samo jednom.'
                : 'Unesite 6-cifreni kod iz vaše autentifikator aplikacije (Google Authenticator, Authy, itd.)'}
            </Typography>

            {twoFactorError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                {twoFactorError}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              label={useBackup ? 'Rezervni kod' : '6-cifreni kod'}
              value={code}
              onChange={(e) => setCode(e.target.value.replaceAll(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              autoFocus
              placeholder={useBackup ? '123456' : '000000'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {useBackup ? <Key sx={{ color: 'text.secondary' }} /> : <Smartphone sx={{ color: 'text.secondary' }} />}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: useBackup ? 'warning.main' : 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: useBackup ? 'warning.main' : 'primary.main',
                  },
                }
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleVerify}
              disabled={loading || !code || code?.length !== 6}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: useBackup
                  ? 'linear-gradient(45deg, #ff7043 30%, #ff8a65 90%)'
                  : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                boxShadow: useBackup
                  ? '0 3px 15px rgba(255, 112, 67, 0.4)'
                  : `0 3px 15px rgba(20, 38, 74, 0.4)`,
                '&:hover': {
                  background: useBackup
                    ? 'linear-gradient(45deg, #f4511e 30%, #ff7043 90%)'
                    : `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                  boxShadow: useBackup
                    ? '0 5px 20px rgba(255, 112, 67, 0.6)'
                    : `0 5px 20px rgba(20, 38, 74, 0.6)`,
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  boxShadow: 'none',
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                useBackup ? 'Verifikuj rezervni kod' : 'Verifikuj kod'
              )}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Button
              fullWidth
              variant="text"
              onClick={handleToggleMode}
              startIcon={useBackup ? <ArrowBack /> : <Key />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: useBackup ? 'primary.main' : 'warning.main',
                '&:hover': {
                  backgroundColor: useBackup ? 'primary.50' : 'warning.50',
                }
              }}
            >
              {useBackup
                ? 'Nazad na autentifikator'
                : 'Koristite rezervni kod'
              }
            </Button>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 2,
                color: 'text.secondary',
                lineHeight: 1.4,
              }}
            >
              {useBackup
                ? 'Rezervni kodovi su za jednokratnu upotrebu. Sačuvajte ih na sigurnom mestu.'
                : 'Nemate pristup telefonu? Koristite rezervni kod koji ste prethodno sačuvali.'
              }
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default TwoFactorModal;
